import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  CheckCircle2,
  CircleDollarSign,
  Database,
  ExternalLink,
  Hourglass,
  KeyRound,
  Power,
  RefreshCw,
  Repeat,
  ServerCog,
  TriangleAlert,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  StatCard,
  TableCard,
  Toolbar,
} from '@/components/shell'
import {
  api,
  ApiError,
  type BuybackSchedulerConfig,
  type BuybackRunRow,
  type OperationsOverview,
  type Paginated,
  type PipelineStatus,
  type SwapQuoteRow,
  type TaxDistributionPayoutConfig,
} from '@/lib/api'
import { explorerAddressUrl, explorerTxUrl, formatDateTime, shortAddress } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/operations')({
  component: OperationsPage,
})

/** 索引流的中文名。查不到就原样显示——新增流不该在界面上变成空白。 */
const STREAM_LABELS: Record<string, string> = {
  launches: '发行',
  trades: '成交',
  holdings: '持仓',
  dividendDistributions: '分红发放',
  dividendClaims: '分红领取',
  taxConversions: '税转换',
  dexSwaps: 'DEX 兑换',
  swapQuotes: '兑换报价',
  buybackRuns: '回购执行',
}

const BUYBACK_KIND_LABELS: Record<string, string> = {
  collect: '拉取收入',
  'claim-dividends': '领取分红',
  buy: '买入',
  burn: '销毁',
}

const BUYBACK_KINDS = ['collect', 'claim-dividends', 'buy', 'burn'] as const

function streamLabel(name: string): string {
  return STREAM_LABELS[name] ?? name
}

function buybackKindLabel(kind: string): string {
  return BUYBACK_KIND_LABELS[kind] ?? kind
}

/**
 * 报价状态没有前端可依赖的枚举——它由 ignix 侧写入，随时可能新增。
 * 所以这里不做「未知就显示未知」，而是原样回显状态码，只额外给出色彩倾向。
 */
function quoteStatusTone(status: string): string {
  const value = status.toUpperCase()
  if (value.includes('FAIL') || value.includes('REVERT')) return 'bg-rose/15 text-rose'
  if (value.includes('EXPIRE') || value.includes('CANCEL')) return 'bg-warn/15 text-warn'
  if (value.includes('EXECUTED') || value.includes('FILLED') || value.includes('SUCCESS')) {
    return 'bg-green/15 text-green'
  }
  return 'bg-edge/10 text-mut'
}

function OperationsPage() {
  const overview = useQuery({
    queryKey: ['operations-overview'],
    queryFn: () => api.get<OperationsOverview>('/operations/overview'),
    staleTime: 30_000,
    retry: false,
  })

  return (
    <Page>
      <PageHeader
        title="运维"
        description="部署一致性、索引管道、回购执行与兑换报价；运行时配置保存后无需重新部署。"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="pressable rounded-apple-sm"
            disabled={overview.isFetching}
            onClick={() => void overview.refetch()}
          >
            <RefreshCw className={cn('size-3.5', overview.isFetching && 'animate-spin')} />
            刷新
          </Button>
        }
      />

      <DeploymentSection query={overview} />
      <TaxDistributionPayoutThresholdControl />
      <BuybackSchedulerControl />
      <TreasuryConsoleReset />
      <PipelineSection />
      <SummarySection query={overview} />
      <StreamSection query={overview} />

      <Tabs defaultValue="buybacks" className="gap-4">
        <TabsList>
          <TabsTrigger value="buybacks" className="h-9 text-[13px]">
            回购执行
          </TabsTrigger>
          <TabsTrigger value="quotes" className="h-9 text-[13px]">
            兑换报价
          </TabsTrigger>
        </TabsList>

        <TabsContent value="buybacks">
          <BuybackRunsPanel />
        </TabsContent>
        <TabsContent value="quotes">
          <SwapQuotesPanel statuses={overview.data?.swapQuotes.byStatus ?? []} />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

function TaxDistributionPayoutThresholdControl() {
  const canManage = useCan()('operations', 'manage')
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [minimumUsd, setMinimumUsd] = useState('')
  const [reason, setReason] = useState('')
  const query = useQuery({
    queryKey: ['tax-distribution-payout-config'],
    queryFn: () =>
      api.get<TaxDistributionPayoutConfig>('/operations/scheduler-jobs/payout-dividends'),
    retry: false,
    refetchInterval: 30_000,
  })
  const update = useMutation({
    mutationFn: () =>
      api.patch('/operations/scheduler-jobs/payout-dividends', {
        minimumUsd: minimumUsd.trim(),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      setEditing(false)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['tax-distribution-payout-config'] })
      toast.success('税收分红自动派发阈值已更新')
    },
    onError: (error) =>
      toast.error('税收分红自动派发阈值更新失败', {
        description: error instanceof Error ? error.message : '请稍后重试',
      }),
  })

  if (query.isPending) return <Skeleton className="h-36 w-full rounded-apple" />
  if (query.isError || !query.data) {
    return (
      <QueryError
        title="读不到税收分红自动派发阈值"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const config = query.data
  const thresholdValid =
    /^\d+(?:\.\d{1,9})?$/.test(minimumUsd.trim()) && !/^0+(?:\.0+)?$/.test(minimumUsd.trim())
  const reasonValid = reason.trim().length >= 10 && reason.trim().length <= 200

  return (
    <section className="surface flex flex-wrap items-center justify-between gap-4 p-6">
      <div className="flex min-w-0 items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <CircleDollarSign className="size-4 text-cyan" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">
              税收分红自动派发阈值
            </h2>
            <Badge variant="secondary" className="font-mono">
              严格超过 ${config.minimumUsd}
            </Badge>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-mut2">
            按每个收款地址的美元应收额分别判断。达到边界值不派发，必须严格超过；未达到时仍可主动领取。
          </p>
          <p className="mt-1 text-[11px] text-dim">
            保存后无需重启，下一轮 5 分钟检查使用新值。
            {config.updatedAt
              ? ` 最近更新：${formatDateTime(config.updatedAt)} · ${config.updatedBy ?? '未知账号'}`
              : ' 尚无人工修改记录，使用系统默认值。'}
          </p>
        </div>
      </div>

      {canManage && (
        <Button
          variant="outline"
          className="pressable rounded-apple"
          onClick={() => {
            setMinimumUsd(config.minimumUsd)
            setReason('')
            setEditing(true)
          }}
        >
          修改阈值
        </Button>
      )}

      <AlertDialog
        open={editing}
        onOpenChange={(open) => {
          if (!open && !update.isPending) {
            setEditing(false)
            setReason('')
          }
        }}
      >
        <AlertDialogContent className="rounded-apple-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] text-txt">
              修改税收分红自动派发阈值
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
              配置会立即写入 IGNIX；下一轮自动派发任务直接读取新值，无需发布或重启。
              已经广播的交易不会被撤回。
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-1.5">
            <label htmlFor="tax-payout-minimum-usd" className="text-[12.5px] font-medium">
              自动派发阈值（USD）
            </label>
            <Input
              id="tax-payout-minimum-usd"
              inputMode="decimal"
              autoComplete="off"
              value={minimumUsd}
              placeholder="例如 1 或 2.5"
              aria-invalid={minimumUsd.length > 0 && !thresholdValid}
              onChange={(event) => setMinimumUsd(event.target.value)}
            />
            <p className="text-[10.5px] text-mut2">必须大于 0，最多 9 位小数；不设置业务上限。</p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="tax-payout-threshold-reason" className="text-[12.5px] font-medium">
                修改理由
              </label>
              <span className="text-[10.5px] text-mut2">{reason.length}/200</span>
            </div>
            <Textarea
              id="tax-payout-threshold-reason"
              value={reason}
              maxLength={200}
              placeholder="说明变更原因、工单编号或决策依据（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px]"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-apple" disabled={update.isPending}>
              返回检查
            </AlertDialogCancel>
            <Button
              className="pressable rounded-apple"
              disabled={!thresholdValid || !reasonValid || update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? '保存中…' : '确认变更'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

/**
 * 破窗:重置独立资金台的登录凭据(admin 专用)。
 *
 * 资金台(/treasury)是自成一体的入口,单用户名 + 单密码、没有邮箱/自助找回。忘记密码时,
 * 唯一的找回路径就是从这里重置——清空后下次打开 /treasury 回到「首次设置」。
 * 只有 operations:manage(admin)可见可点。
 */
function TreasuryConsoleReset() {
  const canManage = useCan()('operations', 'manage')
  const [confirming, setConfirming] = useState(false)
  const reset = useMutation({
    mutationFn: () => api.post('/operations/treasury-console-reset'),
    onSuccess: () => {
      setConfirming(false)
      toast.success('资金台凭据已重置', {
        description: '当前密码立即失效。下次打开 /treasury 会回到「首次设置」,请尽快重新设定。',
      })
    },
    onError: (error) =>
      toast.error('重置失败', {
        description: error instanceof Error ? error.message : '请稍后重试',
      }),
  })

  if (!canManage) return null

  return (
    <section className="surface flex flex-wrap items-center justify-between gap-4 p-6">
      <div className="flex min-w-0 items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <KeyRound className="size-4 text-cyan" />
        </div>
        <div className="min-w-0">
          <h2 className="text-[15px] font-medium tracking-tight text-txt">资金台凭据</h2>
          <p className="mt-1 text-[12px] leading-relaxed text-mut2">
            独立资金台（/treasury）用单独的用户名和密码，与后台账号无关，且没有自助找回。
            忘记密码时从这里重置——清空后下次打开会回到「首次设置」，由你重新设定。
          </p>
        </div>
      </div>

      <Button
        variant="outline"
        className="pressable rounded-apple"
        onClick={() => setConfirming(true)}
      >
        重置资金台凭据
      </Button>

      <AlertDialog
        open={confirming}
        onOpenChange={(open) => {
          if (!open && !reset.isPending) setConfirming(false)
        }}
      >
        <AlertDialogContent className="rounded-apple-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] text-txt">重置资金台凭据？</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-relaxed text-mut">
              重置后当前资金台用户名/密码立即失效，任何人下次打开 /treasury 都会看到「首次设置」并可设定新的凭据。
              仅在忘记密码需要找回时使用。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reset.isPending}>取消</AlertDialogCancel>
            <Button
              variant="destructive"
              className="pressable"
              disabled={reset.isPending}
              onClick={() => reset.mutate()}
            >
              {reset.isPending ? '重置中…' : '确认重置'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

function BuybackSchedulerControl() {
  const canManage = useCan()('operations', 'manage')
  const queryClient = useQueryClient()
  const [nextEnabled, setNextEnabled] = useState<boolean | null>(null)
  const [reason, setReason] = useState('')
  const query = useQuery({
    queryKey: ['buyback-scheduler-config'],
    queryFn: () => api.get<BuybackSchedulerConfig>('/operations/scheduler-jobs/buyback'),
    retry: false,
    refetchInterval: 30_000,
  })
  const update = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch('/operations/scheduler-jobs/buyback', { enabled, reason: reason.trim() }),
    onSuccess: () => {
      setNextEnabled(null)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['buyback-scheduler-config'] })
      toast.success('自动回购运行开关已更新')
    },
    onError: (error) =>
      toast.error('自动回购运行开关更新失败', {
        description: error instanceof Error ? error.message : '请稍后重试',
      }),
  })

  if (query.isPending) return <Skeleton className="h-36 w-full rounded-apple" />
  if (query.isError || !query.data) {
    return (
      <QueryError
        title="读不到自动回购运行开关"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    )
  }

  const config = query.data
  const reasonValid = reason.trim().length >= 10 && reason.trim().length <= 200

  return (
    <section className="surface flex flex-wrap items-center justify-between gap-4 p-6">
      <div className="flex min-w-0 items-start gap-3">
        <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
          <Power className="size-4 text-cyan" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-medium tracking-tight text-txt">自动回购调度</h2>
            <Badge variant={config.enabled ? 'default' : 'secondary'}>
              {config.enabled ? '已开启' : '已关闭'}
            </Badge>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-mut2">
            默认开启。关闭后跳过下一次尚未开始的回购任务，不会撤回已经广播的交易。
          </p>
          <p className="mt-1 text-[11px] text-dim">
            {config.updatedAt
              ? `最近更新：${formatDateTime(config.updatedAt)} · ${config.updatedBy ?? '未知账号'}`
              : '尚无人工修改记录，使用系统默认值'}
          </p>
        </div>
      </div>

      {canManage && (
        <Button
          variant={config.enabled ? 'outline' : 'default'}
          className="pressable rounded-apple"
          onClick={() => setNextEnabled(!config.enabled)}
        >
          {config.enabled ? '关闭自动回购' : '开启自动回购'}
        </Button>
      )}

      <AlertDialog
        open={nextEnabled !== null}
        onOpenChange={(open) => {
          if (!open && !update.isPending) {
            setNextEnabled(null)
            setReason('')
          }
        }}
      >
        <AlertDialogContent className="rounded-apple-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] text-txt">
              {nextEnabled ? '确认开启自动回购？' : '确认关闭自动回购？'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[12.5px] leading-relaxed text-mut">
              {nextEnabled
                ? '开启后，下一次调度到期时会按现有回购配置执行。'
                : '关闭只影响后续尚未开始的任务；已经获取执行权并广播的交易无法由此开关撤回。'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="buyback-scheduler-reason" className="text-[12.5px] font-medium">
                修改理由
              </label>
              <span className="text-[10.5px] text-mut2">{reason.length}/200</span>
            </div>
            <Textarea
              id="buyback-scheduler-reason"
              value={reason}
              maxLength={200}
              placeholder="说明变更原因、工单编号或决策依据（至少 10 个字符）"
              className="min-h-20 resize-none rounded-apple text-[12px]"
              aria-invalid={reason.length > 0 && !reasonValid}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-apple" disabled={update.isPending}>
              返回检查
            </AlertDialogCancel>
            <Button
              className="pressable rounded-apple"
              disabled={nextEnabled === null || !reasonValid || update.isPending}
              onClick={() => nextEnabled !== null && update.mutate(nextEnabled)}
            >
              {update.isPending ? '保存中…' : '确认变更'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

/** 查询出错时统一的呈现：说清楚是哪一块坏了，并给一个重试入口。 */
function QueryError({
  title,
  error,
  onRetry,
}: {
  title: string
  error: unknown
  onRetry: () => void
}) {
  return (
    <div className="surface p-0">
      <EmptyState
        icon={TriangleAlert}
        title={title}
        description={error instanceof ApiError ? error.message : '请稍后重试，或检查后台日志。'}
      />
      <div className="flex justify-center pb-6">
        <Button
          variant="outline"
          size="sm"
          className="pressable rounded-apple-sm"
          onClick={onRetry}
        >
          <RefreshCw className="size-3.5" />
          重试
        </Button>
      </div>
    </div>
  )
}

/** 完整地址/哈希永远挂在 title 上，界面只显示缩略——两者都要，缺一个就没法核对。 */
function RawId({
  value,
  href,
  className,
}: {
  value: string | null
  href?: string
  className?: string
}) {
  if (!value) return <span className="text-dim">—</span>
  const text = <span className="font-mono text-[11px]">{shortAddress(value)}</span>

  if (!href) {
    return (
      <span title={value} className={cn('text-mut', className)}>
        {text}
      </span>
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={value}
      className={cn('inline-flex items-center gap-1 text-mut transition-colors hover:text-cyan', className)}
    >
      {text}
      <ExternalLink className="size-3 shrink-0 opacity-60" />
    </a>
  )
}

function DeploymentSection({ query }: { query: ReturnType<typeof useQuery<OperationsOverview>> }) {
  if (query.isLoading) return <Skeleton className="h-40 w-full rounded-apple" />
  if (query.isError || !query.data) {
    return (
      <QueryError title="读不到运维总览" error={query.error} onRetry={() => void query.refetch()} />
    )
  }

  const { deployment } = query.data
  const { checks, configured, runtime } = deployment
  const consistent = checks?.consistent ?? null

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex items-center gap-2.5">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <ServerCog className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">部署一致性</h2>
            <p className="mt-0.5 text-[12px] text-mut2">
              后台配置的 Manager 与创世块，是否和 ignix 运行时读到的一致。
            </p>
          </div>
        </div>

        <Badge
          className={cn(
            'h-6 text-[11px]',
            consistent === true
              ? 'bg-green/15 text-green hover:bg-green/15'
              : consistent === false
                ? 'bg-rose/15 text-rose hover:bg-rose/15'
                : 'bg-edge/10 text-mut hover:bg-edge/10',
          )}
        >
          {consistent === true ? '一致' : consistent === false ? '不一致' : '无法判定'}
        </Badge>
      </div>

      {deployment.error && (
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            读取 ignix 运行时参数失败：{deployment.error}
            。下方只有「后台配置」一列可信，一致性无法判定。
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <TableHead className="section-label">项目</TableHead>
              <TableHead className="section-label">后台配置</TableHead>
              <TableHead className="section-label">ignix 运行时</TableHead>
              <TableHead className="section-label">结论</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="border-line/40 hover:bg-transparent">
              <TableCell className="text-[12.5px] text-txt">Manager 合约</TableCell>
              <TableCell>
                <RawId
                  value={configured.manager}
                  href={configured.manager ? explorerAddressUrl(configured.manager) : undefined}
                />
              </TableCell>
              <TableCell>
                <RawId
                  value={runtime?.manager ?? null}
                  href={runtime?.manager ? explorerAddressUrl(runtime.manager) : undefined}
                />
              </TableCell>
              <TableCell>
                <MatchMark value={checks?.managerMatches ?? null} />
              </TableCell>
            </TableRow>
            <TableRow className="border-line/40 hover:bg-transparent">
              <TableCell className="text-[12.5px] text-txt">创世块</TableCell>
              <TableCell className="tnum font-mono text-[11px] text-mut">
                {configured.genesisBlock}
              </TableCell>
              <TableCell className="tnum font-mono text-[11px] text-mut">
                {runtime?.genesisBlock ?? '—'}
              </TableCell>
              <TableCell>
                <MatchMark value={checks?.genesisMatches ?? null} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {runtime && runtime.knownQuotes.length > 0 && (
        <div>
          <div className="section-label">运行时已知计价币</div>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {runtime.knownQuotes.map((quote, index) => (
              <li
                key={`${quote.address ?? index}`}
                className="rounded-full bg-edge/[0.06] px-2 py-0.5 text-[10.5px] text-mut"
                title={quote.address ?? undefined}
              >
                <span className="font-medium">{quote.symbol ?? '未知'}</span>
                <span className="ml-1 font-mono text-dim">
                  {quote.address ? shortAddress(quote.address) : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function MatchMark({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-[11.5px] text-mut2">无法判定</span>
  return value ? (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-green">
      <CheckCircle2 className="size-3.5" />
      一致
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-rose">
      <TriangleAlert className="size-3.5" />
      不一致
    </span>
  )
}

/**
 * 索引管道。
 *
 * ⚠️ 服务端的口径是「存在任意租约就 blocked」，**过期的租约同样算**——
 * 过期只代表别的 worker 可以接管，不代表派生表已经重建完。界面必须照搬这个口径，
 * 不能自作主张把过期租约显示成「已解除」。
 */
function PipelineSection() {
  const query = useQuery({
    queryKey: ['operations-pipelines'],
    queryFn: () => api.get<PipelineStatus>('/operations/pipelines'),
    staleTime: 10_000,
    retry: false,
  })

  if (query.isLoading) return <Skeleton className="h-40 w-full rounded-apple" />
  if (query.isError || !query.data) {
    return (
      <QueryError title="读不到索引管道状态" error={query.error} onRetry={() => void query.refetch()} />
    )
  }

  const { blocked, cursors, leases } = query.data

  return (
    <section className="surface space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex items-center gap-2.5">
          <div className="surface-inset flex size-9 shrink-0 items-center justify-center">
            <Database className="size-4 text-cyan" />
          </div>
          <div>
            <h2 className="text-[15px] font-medium tracking-tight text-txt">索引管道</h2>
            <p className="mt-0.5 text-[12px] text-mut2">
              游标推进到哪一块，以及是否有 worker 正持有重建租约。
            </p>
          </div>
        </div>

        <Badge
          className={cn(
            'h-6 text-[11px]',
            blocked ? 'bg-warn/15 text-warn hover:bg-warn/15' : 'bg-green/15 text-green hover:bg-green/15',
          )}
        >
          {blocked ? `占用中 · ${leases.length} 个租约` : '空闲'}
        </Badge>
      </div>

      {blocked && (
        <div className="flex items-start gap-2 rounded-apple bg-warn/[0.07] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-warn">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>
            派生账本可能停在「先删后重建」的中间态，此时读到的业务数据未必完整。
            租约过期只允许新的 worker 接管，不代表当前数据已经安全。
          </span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="section-label">游标</div>
          {cursors.length === 0 ? (
            <p className="mt-2 text-[12px] text-mut2">还没有任何游标记录。</p>
          ) : (
            <ul className="mt-2 divide-y divide-line/40">
              {cursors.map((cursor) => (
                <li key={cursor.name} className="flex items-baseline justify-between gap-3 py-2">
                  <span className="truncate text-[12.5px] text-txt">{cursor.name}</span>
                  <span className="shrink-0 text-right">
                    <span className="tnum block font-mono text-[11.5px] text-mut">
                      #{cursor.lastBlock}
                    </span>
                    <span className="tnum block font-mono text-[10.5px] text-dim">
                      {formatDateTime(cursor.updatedAt)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="section-label">租约</div>
          {leases.length === 0 ? (
            <p className="mt-2 text-[12px] text-mut2">没有 worker 持有租约。</p>
          ) : (
            <ul className="mt-2 divide-y divide-line/40">
              {leases.map((lease) => (
                <li key={lease.name} className="py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-[12.5px] text-txt">{lease.name}</span>
                    <Badge
                      className={cn(
                        'h-5 text-[10px]',
                        lease.expired
                          ? 'bg-warn/15 text-warn hover:bg-warn/15'
                          : 'bg-rose/15 text-rose hover:bg-rose/15',
                      )}
                    >
                      {lease.expired ? '已过期（仍算占用）' : '持有中'}
                    </Badge>
                  </div>
                  <div className="tnum mt-1 font-mono text-[10.5px] leading-relaxed text-dim">
                    holder {lease.holder} · 起始块 #{lease.fromBlock}
                    <br />
                    {formatDateTime(lease.startedAt)} → {formatDateTime(lease.expiresAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}

function SummarySection({ query }: { query: ReturnType<typeof useQuery<OperationsOverview>> }) {
  if (query.isLoading) {
    return (
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-28 rounded-apple" />
        ))}
      </div>
    )
  }
  if (query.isError || !query.data) return null

  const { buybacks, swapQuotes } = query.data

  return (
    <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        icon={Repeat}
        label="回购执行"
        value={buybacks.runs.toLocaleString('en-US')}
        hint={`${buybacks.controllers} 个 Controller · ${buybacks.vaults} 个金库`}
      />
      <StatCard
        icon={TriangleAlert}
        label="24 小时失败"
        value={buybacks.failures24h.toLocaleString('en-US')}
        tone={buybacks.failures24h > 0 ? 'warn' : 'default'}
        hint="ok=false 的执行记录"
      />
      <StatCard
        icon={Activity}
        label="承诺违约"
        value={buybacks.breaches.toLocaleString('en-US')}
        tone={buybacks.breaches > 0 ? 'rose' : 'default'}
        hint="链上实证的授权/余额不足，只增不改"
      />
      <StatCard
        icon={Hourglass}
        label="兑换报价"
        value={swapQuotes.total.toLocaleString('en-US')}
        hint={
          swapQuotes.byStatus.length === 0
            ? '暂无报价'
            : swapQuotes.byStatus.map((row) => `${row.status} ${row.count}`).join(' · ')
        }
      />
    </div>
  )
}

function StreamSection({ query }: { query: ReturnType<typeof useQuery<OperationsOverview>> }) {
  if (query.isLoading) return <Skeleton className="h-64 w-full rounded-apple" />
  if (query.isError || !query.data) return null

  const { streams } = query.data

  return (
    <TableCard>
      <div className="flex items-center justify-between gap-3 px-6 pb-1 pt-5">
        <div>
          <h2 className="text-[15px] font-medium tracking-tight text-txt">索引数据量</h2>
          <p className="mt-0.5 text-[12px] text-mut2">
            按创世块过滤后的各条数据流。最后时间长期不动通常意味着索引停了。
          </p>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-line/50 hover:bg-transparent">
            <TableHead className="section-label pl-6">数据流</TableHead>
            <TableHead className="section-label text-right">条数</TableHead>
            <TableHead className="section-label">最后时间</TableHead>
            <TableHead className="section-label pr-6">最后区块</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {streams.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4}>
                <EmptyState
                  icon={Database}
                  title="没有可统计的数据流"
                  description="索引还没有写入任何数据，或创世块之后尚无事件。"
                />
              </TableCell>
            </TableRow>
          )}
          {streams.map((stream) => (
            <TableRow key={stream.name} className="row-hover border-line/40">
              <TableCell className="pl-6">
                <span className="text-[12.5px] text-txt">{streamLabel(stream.name)}</span>
                <span className="ml-2 font-mono text-[10.5px] text-dim">{stream.name}</span>
              </TableCell>
              <TableCell className="tnum text-right font-mono text-[11.5px] text-txt">
                {stream.count.toLocaleString('en-US')}
              </TableCell>
              <TableCell
                className={cn(
                  'tnum whitespace-nowrap font-mono text-[11px]',
                  stream.lastAt ? 'text-mut' : 'text-dim',
                )}
              >
                {formatDateTime(stream.lastAt)}
              </TableCell>
              <TableCell className="tnum pr-6 font-mono text-[11px] text-mut2">
                {stream.lastBlock ? `#${stream.lastBlock}` : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  )
}

function BuybackRunsPanel() {
  const [page, setPage] = useState(1)
  const [kind, setKind] = useState('all')
  const [result, setResult] = useState('all')

  const query = useQuery({
    queryKey: ['operations-buyback-runs', { page, kind, result }],
    queryFn: () =>
      api.get<Paginated<BuybackRunRow>>('/operations/buyback-runs', {
        page,
        perPage: 20,
        kind: kind === 'all' ? undefined : kind,
        ok: result === 'failed' ? 'false' : result === 'ok' ? 'true' : undefined,
        breach: result === 'breach' ? 'true' : undefined,
      }),
    retry: false,
  })

  const reset = (apply: () => void) => {
    apply()
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <Toolbar trailing={<Count value={query.data?.total} />}>
        <Select value={kind} onValueChange={(value) => reset(() => setKind(value))}>
          <SelectTrigger className="h-9 w-36 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部类型</SelectItem>
            {BUYBACK_KINDS.map((value) => (
              <SelectItem key={value} value={value}>
                {buybackKindLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={result} onValueChange={(value) => reset(() => setResult(value))}>
          <SelectTrigger className="h-9 w-32 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部结果</SelectItem>
            <SelectItem value="ok">仅成功</SelectItem>
            <SelectItem value="failed">仅失败</SelectItem>
            <SelectItem value="breach">仅违约</SelectItem>
          </SelectContent>
        </Select>
      </Toolbar>

      {query.isError ? (
        <QueryError
          title="读不到回购执行记录"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow className="border-line/50 hover:bg-transparent">
                  <TableHead className="section-label pl-6">时间</TableHead>
                  <TableHead className="section-label">类型</TableHead>
                  <TableHead className="section-label">结果</TableHead>
                  <TableHead className="section-label">金库 / Controller</TableHead>
                  <TableHead className="section-label">交易</TableHead>
                  <TableHead className="section-label pr-6">失败原因</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading &&
                  Array.from({ length: 6 }, (_, index) => (
                    <TableRow key={index} className="border-line/40">
                      <TableCell colSpan={6} className="px-6 py-2.5">
                        <Skeleton className="h-7 w-full rounded-apple-sm" />
                      </TableCell>
                    </TableRow>
                  ))}

                {query.data?.data.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6}>
                      <EmptyState
                        icon={Repeat}
                        title="没有符合条件的回购执行"
                        description="换一个类型或结果筛选看看；也可能这条流水线还没有跑过。"
                      />
                    </TableCell>
                  </TableRow>
                )}

                {query.data?.data.map((run) => (
                  <TableRow key={run.id} className="row-hover border-line/40 align-top">
                    <TableCell className="tnum whitespace-nowrap pl-6 font-mono text-[11px] text-mut2">
                      {formatDateTime(run.createdAt)}
                    </TableCell>
                    <TableCell>
                      <span className="text-[12px] text-txt">{buybackKindLabel(run.kind)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          className={cn(
                            'h-5 text-[10.5px]',
                            run.ok
                              ? 'bg-green/15 text-green hover:bg-green/15'
                              : 'bg-rose/15 text-rose hover:bg-rose/15',
                          )}
                        >
                          {run.ok ? '成功' : '失败'}
                        </Badge>
                        {run.breach && (
                          <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                            违约
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <RawId
                          value={run.vault}
                          href={run.vault ? explorerAddressUrl(run.vault) : undefined}
                          className="block"
                        />
                        <RawId
                          value={run.controller}
                          href={run.controller ? explorerAddressUrl(run.controller) : undefined}
                          className="block opacity-70"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <RawId
                        value={run.txHash}
                        href={run.txHash ? explorerTxUrl(run.txHash) : undefined}
                      />
                    </TableCell>
                    <TableCell className="max-w-xs pr-6">
                      {run.error ? (
                        <span
                          title={run.error}
                          className="line-clamp-2 text-[11px] leading-relaxed text-rose"
                        >
                          {run.error}
                        </span>
                      ) : (
                        <span className="text-[11px] text-dim">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>

          <Pager page={page} pageCount={query.data?.pageCount ?? 1} onChange={setPage} />
        </>
      )}
    </div>
  )
}

function SwapQuotesPanel({
  statuses,
}: {
  statuses: readonly { status: string; count: number }[]
}) {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('all')
  const [owner, setOwner] = useState('')

  const trimmedOwner = owner.trim()
  const ownerFilter = /^0x[0-9a-fA-F]{40}$/.test(trimmedOwner) ? trimmedOwner : undefined

  const query = useQuery({
    queryKey: ['operations-swap-quotes', { page, status, owner: ownerFilter }],
    queryFn: () =>
      api.get<Paginated<SwapQuoteRow>>('/operations/swap-quotes', {
        page,
        perPage: 20,
        status: status === 'all' ? undefined : status,
        owner: ownerFilter,
      }),
    retry: false,
  })

  return (
    <div className="space-y-4">
      <Toolbar trailing={<Count value={query.data?.total} />}>
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-40 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部状态</SelectItem>
            {/* 状态取值由 ignix 侧决定，这里直接用总览里真实出现过的那些 */}
            {statuses.map((row) => (
              <SelectItem key={row.status} value={row.status}>
                {row.status}（{row.count}）
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={owner}
          placeholder="按发起地址筛选（0x…）"
          spellCheck={false}
          autoComplete="off"
          className="h-9 w-64 rounded-apple font-mono text-[12px]"
          onChange={(event) => {
            setOwner(event.target.value)
            setPage(1)
          }}
        />
        {trimmedOwner.length > 0 && !ownerFilter && (
          <span className="text-[11px] text-warn">地址不完整，暂未生效</span>
        )}
      </Toolbar>

      {query.isError ? (
        <QueryError
          title="读不到兑换报价"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <TableCard>
            <Table>
              <TableHeader>
                <TableRow className="border-line/50 hover:bg-transparent">
                  <TableHead className="section-label pl-6">更新时间</TableHead>
                  <TableHead className="section-label">状态</TableHead>
                  <TableHead className="section-label">报价 ID</TableHead>
                  <TableHead className="section-label">发起地址</TableHead>
                  <TableHead className="section-label">路线</TableHead>
                  <TableHead className="section-label pr-6">执行</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading &&
                  Array.from({ length: 6 }, (_, index) => (
                    <TableRow key={index} className="border-line/40">
                      <TableCell colSpan={6} className="px-6 py-2.5">
                        <Skeleton className="h-7 w-full rounded-apple-sm" />
                      </TableCell>
                    </TableRow>
                  ))}

                {query.data?.data.length === 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={6}>
                      <EmptyState
                        icon={Hourglass}
                        title="没有符合条件的报价"
                        description="换一个状态或清空地址筛选再试；报价会在过期后仍然保留。"
                      />
                    </TableCell>
                  </TableRow>
                )}

                {query.data?.data.map((quote) => (
                  <TableRow key={quote.quoteId} className="row-hover border-line/40 align-top">
                    <TableCell className="tnum whitespace-nowrap pl-6 font-mono text-[11px] text-mut2">
                      {formatDateTime(quote.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge className={cn('h-5 font-mono text-[10.5px]', quoteStatusTone(quote.status))}>
                          {quote.status}
                        </Badge>
                        {quote.receiptStatus && (
                          <div className="font-mono text-[10px] text-dim">
                            回执 {quote.receiptStatus}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <RawId value={quote.quoteId} />
                      <div className="tnum mt-0.5 font-mono text-[10px] text-dim">
                        块 #{quote.quotedAtBlock} · 到期 {formatDateTime(quote.expiresAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <RawId value={quote.owner} href={explorerAddressUrl(quote.owner)} />
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-[10.5px] text-dim">
                        <div className="flex items-center gap-1">
                          <RawId
                            value={quote.tokenIn}
                            href={explorerAddressUrl(quote.tokenIn)}
                          />
                          <span aria-hidden="true">→</span>
                          <RawId
                            value={quote.tokenOut}
                            href={explorerAddressUrl(quote.tokenOut)}
                          />
                        </div>
                        <div>
                          {quote.serverSuggestedVenue ? `建议 ${quote.serverSuggestedVenue}` : '未给出建议'}
                          {quote.selectionReason ? ` · ${quote.selectionReason}` : ''}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="pr-6">
                      {quote.txHash ? (
                        <div className="space-y-0.5">
                          <RawId value={quote.txHash} href={explorerTxUrl(quote.txHash)} />
                          <div className="font-mono text-[10px] text-dim">
                            {quote.executedVenue ?? '—'}
                            {quote.executedBlock ? ` · 块 #${quote.executedBlock}` : ''}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-dim">未执行</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCard>

          <Pager page={page} pageCount={query.data?.pageCount ?? 1} onChange={setPage} />
        </>
      )}
    </div>
  )
}
